/**
 * orgPitch/client — the in-app "hire prest0n first" lane for HIRE-REQUIRED
 * created organizations (task #75, 2026-08-18 owner directive).
 *
 * Inside an unhired created org, prest0n behaves like the signed-out guest
 * lane — page-aware, genuinely useful about prest0 — but its only call to
 * action is HIRING. This client resolves whether the ACTIVE workspace is
 * hire-required (cached ~60s; the server re-verifies on every turn) and runs
 * one pitch turn against the authed orgPitchChatTurn endpoint.
 *
 * Failure posture: resolution errors resolve to null (normal lanes), and a
 * failed pitch call falls through to the normal lanes too — where the
 * server-side paymentGate still refuses real work with the honest hire
 * message. The user always gets an answer; work never silently happens.
 */

import { getAuth } from 'firebase/auth';
import { doc, getDoc, getFirestore } from 'firebase/firestore';

export const ORG_PITCH_SOURCE_TAG = 'org_pitch_reply';

function pitchUrl(): string {
  const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
  return `https://us-central1-${projectId}.cloudfunctions.net/orgPitchChatTurn`;
}

interface HireRequiredWorkspace {
  accountId: string;
  orgName: string;
}

let cache: { atMs: number; uid: string; value: HireRequiredWorkspace | null } | null = null;
const CACHE_TTL_MS = 60_000;

/** Null = the active workspace is NOT hire-required (or unknown) — use the
 *  normal lanes. Only created-org ids (co_…) can ever be hire-required. */
export async function resolveHireRequiredWorkspace(): Promise<HireRequiredWorkspace | null> {
  try {
    const user = getAuth().currentUser;
    if (!user) return null;
    const now = Date.now();
    if (cache && cache.uid === user.uid && now - cache.atMs < CACHE_TTL_MS) return cache.value;
    const db = getFirestore();
    const backSnap = await getDoc(doc(db, 'users', user.uid, 'account_membership', 'main'));
    const accountId = String((backSnap.data() as any)?.accountId || '').trim();
    let value: HireRequiredWorkspace | null = null;
    if (accountId.startsWith('co_')) {
      const profSnap = await getDoc(doc(db, 'accounts', accountId, 'profile', 'main'));
      const prof: any = profSnap.exists() ? profSnap.data() : null;
      if (prof?.hireRequired === true) {
        value = { accountId, orgName: String(prof?.firmName || 'your organization') };
      }
    }
    cache = { atMs: now, uid: user.uid, value };
    return value;
  } catch {
    return null; // fail toward the normal lanes — the server gate backstops
  }
}

/** Drop the cache (e.g. right after a hire) so the next turn re-resolves. */
export function clearHireRequiredCache(): void {
  cache = null;
}

export async function runOrgPitchTurn(params: {
  accountId: string;
  message: string;
  history?: Array<{ role: 'user' | 'assistant'; content: string }>;
}): Promise<string> {
  const user = getAuth().currentUser;
  if (!user) throw new Error('not signed in');
  const token = await user.getIdToken();
  const resp = await fetch(pitchUrl(), {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      accountId: params.accountId,
      message: params.message,
      route: window.location.pathname,
      history: params.history ?? [],
    }),
  });
  const data = await resp.json().catch(() => ({}));
  if (resp.status === 409 && data?.code === 'not-hire-required') {
    // The org got hired since we cached — clear and let the caller rerun.
    clearHireRequiredCache();
    throw new Error('workspace-active');
  }
  if (!resp.ok) throw new Error(String(data?.error || `HTTP ${resp.status}`));
  const reply = typeof data?.reply === 'string' ? data.reply.trim() : '';
  if (!reply) throw new Error('empty pitch reply');
  return reply;
}
